import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canEditData, categoryLabel, formatDate, formatIDR, today } from "../lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Loader2, Pencil, Search, Trash2, Upload, Download, FileText, FileSpreadsheet, Camera, FileDown, ReceiptText } from "lucide-react";

const emptyForm = (firstCategory) => ({
  tx_date: today(),
  category: firstCategory,
  amount: "",
  party: "",
  description: "",
  contact_id: "",
});

export default function TxPage({ title, subtitle, tabs, direction, partyField, partyLabel, testPrefix }) {
  const { user } = useAuth();
  const [tab, setTab] = useState(tabs[0].key);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [month, setMonth] = useState("");
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm(tabs[0].categories[0].key));
  const [contacts, setContacts] = useState([]);
  const fileRef = useRef(null);
  const [photoTx, setPhotoTx] = useState(null);
  const photoInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [notaTx, setNotaTx] = useState(null);
  const notaInputRef = useRef(null);
  const [uploadingNota, setUploadingNota] = useState(false);

  const activeTab = tabs.find((t) => t.key === tab);

  const fetchTxs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { direction };
      if (month) {
        params.start = `${month}-01`;
        params.end = `${month}-31`;
      }
      if (search) params.search = search;
      const { data } = await api.get("/transactions", { params });
      setTxs(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [direction, month, search]);

  useEffect(() => {
    const t = setTimeout(fetchTxs, 250);
    return () => clearTimeout(t);
  }, [fetchTxs]);

  useEffect(() => {
    if (direction === "in") {
      api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {});
    }
  }, [direction]);

  const rows = useMemo(() => {
    const keys = activeTab.categories.map((c) => c.key);
    return txs.filter((t) => keys.includes(t.category));
  }, [txs, activeTab]);

  const totalRows = useMemo(() => rows.reduce((s, t) => s + t.amount, 0), [rows]);

  const handleTabChange = (v) => {
    setTab(v);
    const newTab = tabs.find((t) => t.key === v);
    setEditingId(null);
    setForm(emptyForm(newTab.categories[0].key));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category || !form.amount || Number(form.amount) <= 0) {
      toast.error("Lengkapi kategori dan jumlah dengan benar");
      return;
    }
    setSaving(true);
    const payload = {
      tx_date: form.tx_date,
      category: form.category,
      amount: Number(form.amount),
      donor: partyField === "donor" ? form.party : "",
      recipient: partyField === "recipient" ? form.party : "",
      description: form.description,
      contact_id: form.contact_id || null,
    };
    try {
      if (editingId) {
        await api.put(`/transactions/${editingId}`, payload);
        toast.success("Transaksi berhasil diperbarui");
      } else {
        const { data } = await api.post("/transactions", payload);
        toast.success("Transaksi berhasil disimpan");
        if (data.wa_notif) {
          toast.info(`Notifikasi WA: ${data.wa_notif}`);
        }
      }
      setEditingId(null);
      setForm(emptyForm(activeTab.categories[0].key));
      fetchTxs();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (tx) => {
    setEditingId(tx.tx_id);
    setForm({
      tx_date: tx.tx_date,
      category: tx.category,
      amount: String(tx.amount),
      party: partyField === "donor" ? tx.donor || "" : tx.recipient || "",
      description: tx.description || "",
      contact_id: tx.contact_id || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (tx) => {
    if (confirmDelete !== tx.tx_id) {
      setConfirmDelete(tx.tx_id);
      setTimeout(() => setConfirmDelete(null), 3000);
      return;
    }
    try {
      await api.delete(`/transactions/${tx.tx_id}`);
      toast.success("Transaksi dihapus");
      setConfirmDelete(null);
      fetchTxs();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleExport = async (format) => {
    try {
      const params = { direction, format };
      if (month) {
        params.start = `${month}-01`;
        params.end = `${month}-31`;
      }
      if (search) params.search = search;
      const res = await api.get("/transactions/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `lazisnu-${direction === "in" ? "penerimaan" : "pentasyarufan"}-${month || "semua"}.${format === "xlsx" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Data berhasil diunduh (${format.toUpperCase()})`);
    } catch (e) {
      toast.error("Gagal mengunduh data");
    }
  };

  const handleTemplate = async () => {
    try {
      const res = await api.get("/transactions/template", { params: { direction }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `contoh-impor-${direction === "in" ? "penerimaan" : "pentasyarufan"}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Gagal mengunduh contoh file");
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/transactions/import?direction=${direction}`, fd);
      if (data.failed > 0) {
        toast.warning(`Impor: ${data.imported} berhasil, ${data.failed} gagal. ${data.errors?.[0] || ""}`);
      } else {
        toast.success(`${data.imported} transaksi berhasil diimpor`);
      }
      fetchTxs();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || !photoTx) return;
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    setUploadingPhoto(true);
    try {
      const { data } = await api.post(`/transactions/${photoTx.tx_id}/photos`, fd);
      toast.success(`${files.length} foto berhasil diunggah`);
      setPhotoTx((prev) => (prev ? { ...prev, photos: data.photos } : prev));
      fetchTxs();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoDelete = async (photoId) => {
    if (!photoTx) return;
    try {
      await api.delete(`/transactions/${photoTx.tx_id}/photos/${photoId}`);
      setPhotoTx({ ...photoTx, photos: (photoTx.photos || []).filter((p) => p.photo_id !== photoId) });
      toast.success("Foto dihapus");
      fetchTxs();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleReceiptDownload = async (tx) => {
    try {
      const res = await api.get(`/transactions/${tx.tx_id}/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `kwitansi-${tx.tx_id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Kwitansi PDF berhasil diunduh");
    } catch (e) {
      toast.error("Gagal mengunduh kwitansi");
    }
  };

  const handleNotaUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length || !notaTx) return;
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    setUploadingNota(true);
    try {
      const { data } = await api.post(`/transactions/${notaTx.tx_id}/receipts`, fd);
      toast.success(`${files.length} kwitansi/nota berhasil diunggah`);
      setNotaTx((prev) => (prev ? { ...prev, receipts: data.receipts } : prev));
      fetchTxs();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingNota(false);
    }
  };

  const handleNotaDelete = async (docId) => {
    if (!notaTx) return;
    try {
      await api.delete(`/transactions/${notaTx.tx_id}/receipts/${docId}`);
      setNotaTx({ ...notaTx, receipts: (notaTx.receipts || []).filter((d) => d.doc_id !== docId) });
      toast.success("Nota dihapus");
      fetchTxs();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid={`${testPrefix}-page`}>
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-800">{title}</h1>
        <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
      </div>

      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList data-testid={`${testPrefix}-tabs`}>
          {tabs.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`${testPrefix}-tab-${t.key}`}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1 h-fit">
          <CardHeader>
            <CardTitle className="text-lg">{editingId ? "Ubah Transaksi" : "Tambah Transaksi"}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid={`${testPrefix}-form`}>
              <div className="space-y-2">
                <Label>Tanggal</Label>
                <Input
                  type="date"
                  data-testid={`${testPrefix}-date-input`}
                  value={form.tx_date}
                  onChange={(e) => setForm({ ...form, tx_date: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Kategori</Label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm({ ...form, category: v })}
                >
                  <SelectTrigger data-testid={`${testPrefix}-category-select`}>
                    <SelectValue placeholder="Pilih kategori" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeTab.categories.map((c) => (
                      <SelectItem key={c.key} value={c.key} data-testid={`${testPrefix}-category-${c.key}`}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Jumlah (Rp)</Label>
                <Input
                  type="number"
                  min="1"
                  data-testid={`${testPrefix}-amount-input`}
                  placeholder="0"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  required
                />
              </div>
              {direction === "in" && (
                <div className="space-y-2">
                  <Label>Ambil dari Database (opsional)</Label>
                  <Select
                    value={form.contact_id || ""}
                    onValueChange={(v) => {
                      const c = contacts.find((x) => x.contact_id === v);
                      setForm({ ...form, contact_id: v, party: c ? c.name : form.party });
                    }}
                  >
                    <SelectTrigger data-testid={`${testPrefix}-contact-select`}>
                      <SelectValue placeholder="Pilih Munfiq/Muzaki/Ranting..." />
                    </SelectTrigger>
                    <SelectContent>
                      {contacts.map((c) => (
                        <SelectItem key={c.contact_id} value={c.contact_id} data-testid={`${testPrefix}-contact-${c.contact_id}`}>
                          {c.name} ({c.type_label})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>{partyLabel}</Label>
                <Input
                  data-testid={`${testPrefix}-party-input`}
                  placeholder={partyLabel}
                  value={form.party}
                  onChange={(e) => setForm({ ...form, party: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Keterangan</Label>
                <Textarea
                  data-testid={`${testPrefix}-description-input`}
                  placeholder="Keterangan tambahan (opsional)"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="submit"
                  data-testid={`${testPrefix}-submit-button`}
                  disabled={saving}
                  className="flex-1 bg-emerald-800 hover:bg-emerald-700 text-white"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingId ? "Simpan Perubahan" : "Simpan"}
                </Button>
                {editingId && (
                  <Button
                    type="button"
                    variant="outline"
                    data-testid={`${testPrefix}-cancel-edit-button`}
                    onClick={() => {
                      setEditingId(null);
                      setForm(emptyForm(activeTab.categories[0].key));
                    }}
                  >
                    Batal
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <CardTitle className="text-lg flex-1">Riwayat {activeTab.label}</CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImportFile} data-testid={`${testPrefix}-import-file-input`} />
                <Button type="button" variant="outline" size="sm" data-testid={`${testPrefix}-template-button`} onClick={handleTemplate}>
                  <Download className="mr-1.5 h-3.5 w-3.5" />Contoh Excel
                </Button>
                <Button type="button" variant="outline" size="sm" data-testid={`${testPrefix}-import-button`} onClick={() => fileRef.current?.click()}>
                  <Upload className="mr-1.5 h-3.5 w-3.5" />Impor Excel
                </Button>
                <Button type="button" variant="outline" size="sm" data-testid={`${testPrefix}-export-pdf-button`} onClick={() => handleExport("pdf")}>
                  <FileText className="mr-1.5 h-3.5 w-3.5 text-red-600" />PDF
                </Button>
                <Button type="button" variant="outline" size="sm" data-testid={`${testPrefix}-export-excel-button`} onClick={() => handleExport("xlsx")}>
                  <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5 text-emerald-700" />Excel
                </Button>
                <Input
                  type="month"
                  data-testid={`${testPrefix}-month-filter`}
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  className="w-[160px]"
                />
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    data-testid={`${testPrefix}-search-input`}
                    placeholder="Cari..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-8 w-[160px]"
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[520px] overflow-auto">
              <Table data-testid={`${testPrefix}-table`}>
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow>
                    <TableHead>Tanggal</TableHead>
                    <TableHead>Kategori</TableHead>
                    <TableHead>{partyLabel}</TableHead>
                    <TableHead>Keterangan</TableHead>
                    <TableHead className="text-right">Jumlah</TableHead>
                    <TableHead className="text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-10 text-slate-400">
                        <Loader2 className="h-5 w-5 animate-spin inline-block" />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-10 text-slate-400" data-testid={`${testPrefix}-empty`}>
                        Belum ada transaksi {activeTab.label}
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((tx) => (
                      <TableRow key={tx.tx_id} className="transition-colors duration-200" data-testid={`${testPrefix}-row-${tx.tx_id}`}>
                        <TableCell className="whitespace-nowrap">{formatDate(tx.tx_date)}</TableCell>
                        <TableCell>
                          <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${
                            tx.fund === "zakat" ? "bg-yellow-100 text-yellow-800" : "bg-emerald-100 text-emerald-800"
                          }`}>
                            {categoryLabel(tx.category)}
                          </span>
                        </TableCell>
                        <TableCell className="max-w-[140px] truncate">{partyField === "donor" ? tx.donor || "-" : tx.recipient || "-"}</TableCell>
                        <TableCell className="max-w-[160px] truncate text-slate-500">{tx.description || "-"}</TableCell>
                        <TableCell className={`text-right font-semibold whitespace-nowrap ${direction === "in" ? "text-emerald-700" : "text-red-600"}`}>
                          {direction === "in" ? "+" : "-"}{formatIDR(tx.amount)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                          {direction === "in" && (
                            <button
                              data-testid={`${testPrefix}-receipt-${tx.tx_id}`}
                              onClick={() => handleReceiptDownload(tx)}
                              title="Unduh Kwitansi (PDF A5)"
                              className="rounded-md p-1.5 text-slate-400 transition-colors duration-200 hover:bg-yellow-50 hover:text-yellow-700"
                            >
                              <FileDown className="h-4 w-4" />
                            </button>
                          )}
                          {direction === "out" && (
                            <button
                              data-testid={`${testPrefix}-nota-${tx.tx_id}`}
                              onClick={() => setNotaTx(tx)}
                              title="Kwitansi/Nota data dukung"
                              className="relative rounded-md p-1.5 text-slate-400 transition-colors duration-200 hover:bg-yellow-50 hover:text-yellow-700"
                            >
                              <ReceiptText className="h-4 w-4" />
                              {(tx.receipts?.length || 0) > 0 && (
                                <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-yellow-600 px-1 text-[9px] font-bold text-white">
                                  {tx.receipts.length}
                                </span>
                              )}
                            </button>
                          )}
                          {direction === "out" && (
                            <button
                              data-testid={`${testPrefix}-photos-${tx.tx_id}`}
                              onClick={() => setPhotoTx(tx)}
                              title="Foto dokumentasi pentasyarufan"
                              className="relative rounded-md p-1.5 text-slate-400 transition-colors duration-200 hover:bg-sky-50 hover:text-sky-700"
                            >
                              <Camera className="h-4 w-4" />
                              {(tx.photos?.length || 0) > 0 && (
                                <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-sky-600 px-1 text-[9px] font-bold text-white">
                                  {tx.photos.length}
                                </span>
                              )}
                            </button>
                          )}
                          {canEditData(user?.role) && (
                          <>
                            <button
                              data-testid={`${testPrefix}-edit-${tx.tx_id}`}
                              onClick={() => handleEdit(tx)}
                              className="rounded-md p-1.5 text-slate-400 transition-colors duration-200 hover:bg-emerald-50 hover:text-emerald-700"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              data-testid={`${testPrefix}-delete-${tx.tx_id}`}
                              onClick={() => handleDelete(tx)}
                              className={`rounded-md p-1.5 transition-colors duration-200 ${
                                confirmDelete === tx.tx_id
                                  ? "bg-red-600 text-white"
                                  : "text-slate-400 hover:bg-red-50 hover:text-red-600"
                              }`}
                              title={confirmDelete === tx.tx_id ? "Klik lagi untuk konfirmasi" : "Hapus"}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                          )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            {!loading && rows.length > 0 && (
              <div className="flex justify-between items-center border-t border-slate-200 px-4 py-3 text-sm" data-testid={`${testPrefix}-total`}>
                <span className="text-slate-500">{rows.length} transaksi</span>
                <span className="font-semibold text-slate-800">Total: {formatIDR(totalRows)}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!photoTx} onOpenChange={(o) => !o && setPhotoTx(null)}>
        <DialogContent className="max-w-2xl" data-testid="photo-dialog">
          <DialogHeader>
            <DialogTitle>Foto Dokumentasi — {photoTx ? categoryLabel(photoTx.category) : ""}</DialogTitle>
          </DialogHeader>
          <input ref={photoInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} data-testid="photo-file-input" />
          <div className="mb-3">
            <Button type="button" variant="outline" size="sm" data-testid="photo-upload-button" disabled={uploadingPhoto} onClick={() => photoInputRef.current?.click()}>
              {uploadingPhoto ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1.5 h-3.5 w-3.5" />}
              Unggah Foto
            </Button>
          </div>
          {(photoTx?.photos || []).length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400" data-testid="photo-empty">
              Belum ada foto. Foto yang diunggah akan tampil di slideshow dasbor publik.
            </p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[400px] overflow-auto">
              {photoTx.photos.map((p) => (
                <div key={p.photo_id} className="group relative overflow-hidden rounded-lg border border-slate-200">
                  <img src={`${API}/files/${p.storage_path}`} alt={p.filename} className="aspect-video w-full object-cover" />
                  {canEditData(user?.role) && (
                    <button
                      data-testid={`photo-delete-${p.photo_id}`}
                      onClick={() => handlePhotoDelete(p.photo_id)}
                      className="absolute right-1 top-1 rounded-md bg-red-600/90 p-1 text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!notaTx} onOpenChange={(o) => !o && setNotaTx(null)}>
        <DialogContent className="max-w-xl" data-testid="nota-dialog">
          <DialogHeader>
            <DialogTitle>Kwitansi / Nota — {notaTx ? categoryLabel(notaTx.category) : ""}</DialogTitle>
          </DialogHeader>
          <input ref={notaInputRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={handleNotaUpload} data-testid="nota-file-input" />
          <div className="mb-3">
            <Button type="button" variant="outline" size="sm" data-testid="nota-upload-button" disabled={uploadingNota} onClick={() => notaInputRef.current?.click()}>
              {uploadingNota ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1.5 h-3.5 w-3.5" />}
              Unggah Kwitansi/Nota
            </Button>
          </div>
          {(notaTx?.receipts || []).length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400" data-testid="nota-empty">
              Belum ada kwitansi/nota sebagai data dukung pengeluaran dana.
            </p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-auto">
              {notaTx.receipts.map((doc) => (
                <div key={doc.doc_id} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2">
                  <FileText className="h-4 w-4 shrink-0 text-emerald-800" />
                  <span className="flex-1 truncate text-sm text-slate-700">{doc.filename}</span>
                  <a href={`${API}/files/${doc.storage_path}`} target="_blank" rel="noreferrer" data-testid={`nota-open-${doc.doc_id}`}>
                    <Button variant="outline" size="sm">Buka</Button>
                  </a>
                  {canEditData(user?.role) && (
                    <button
                      data-testid={`nota-delete-${doc.doc_id}`}
                      onClick={() => handleNotaDelete(doc.doc_id)}
                      className="rounded-md p-1.5 text-slate-400 transition-colors duration-200 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
